import React, { useState } from 'react'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { useProgramConfig } from 'hooks/usePartnerAdmin'
import { createProgramConfig } from 'submission/lib/partner-api'
import PartnersLayout from './PartnersLayout'
import { validateConfig } from './configValidation'
import { bipsToPercent, formatDate, percentToBips } from './format'
import { useResyncingDraft } from './useResyncingDraft'
import type { ProgramConfig as Config, Tier } from './types'
import { TIERS } from './types'

type TierDraft = {
  name: Tier
  min_active_referrals: string
  share_percent: string
  signup_bonus_usdc: string
  campaign_link_limit: string
}

export type Draft = {
  tiers: TierDraft[]
  signup_min_deposit_usdc: string
  signup_bonus_hold_days: string
  attribution_window_hours: string
}

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'

export const toDraft = (c: Config): Draft => ({
  tiers: TIERS.map((name) => {
    const t = c.tiers.find((x) => x.name === name)
    return {
      name,
      min_active_referrals: String(t?.min_active_referrals ?? 0),
      share_percent: bipsToPercent(t?.share_bips ?? 0),
      signup_bonus_usdc: t?.signup_bonus_usdc ?? '0',
      campaign_link_limit: String(t?.campaign_link_limit ?? 0),
    }
  }),
  signup_min_deposit_usdc: c.signup_min_deposit_usdc,
  signup_bonus_hold_days: String(c.signup_bonus_hold_days),
  attribution_window_hours: String(c.attribution_window_hours),
})

const toInt = (s: string): number => (s.trim() === '' ? NaN : Number(s))

// Percent → bips happens here, once. NaN for anything unparsable so
// validateConfig reports it.
const fromDraft = (d: Draft): Config => ({
  tiers: d.tiers.map((t) => ({
    name: t.name,
    min_active_referrals: toInt(t.min_active_referrals),
    share_bips: percentToBips(t.share_percent) ?? NaN,
    signup_bonus_usdc: t.signup_bonus_usdc.trim(),
    campaign_link_limit: toInt(t.campaign_link_limit),
  })),
  signup_min_deposit_usdc: d.signup_min_deposit_usdc.trim(),
  signup_bonus_hold_days: toInt(d.signup_bonus_hold_days),
  attribution_window_hours: toInt(d.attribution_window_hours),
})

const ProgramConfig: React.FC = () => {
  const { showToast } = useToast()
  const { data, error, isLoading, mutate } = useProgramConfig()
  const [draft, setDraft] = useResyncingDraft(data?.config, toDraft)
  const [errors, setErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const setTier = (i: number, patch: Partial<TierDraft>) =>
    setDraft(
      (d) =>
        d && {
          ...d,
          tiers: d.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)),
        },
    )

  const publish = async () => {
    if (!draft) return
    const cfg = fromDraft(draft)
    const errs = validateConfig(cfg)
    setErrors(errs)
    if (errs.length > 0) return
    setSaving(true)
    try {
      const v = await createProgramConfig(cfg)
      showToast({
        type: 'success',
        message: `Config version ${v.version} published`,
      })
      mutate()
    } catch (e) {
      showToast({ type: 'error', message: (e as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <PartnersLayout
      title="Program config"
      actions={
        <Button onClick={publish} disabled={!draft || saving}>
          Publish new version
        </Button>
      }
    >
      {error && (
        <div className="text-sm text-red-600 mb-3">
          Failed to load: {(error as Error).message}
        </div>
      )}
      {isLoading && <div className="text-sm text-neutral-600">Loading…</div>}
      {data && (
        <div className="text-sm text-neutral-600 mb-4">
          Current: version {data.version}, published{' '}
          {formatDate(data.created_at)} by {data.created_by}. Publishing creates
          a new version; accrued ledger entries keep the version they were
          created under.
        </div>
      )}
      {errors.length > 0 && (
        <ul className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 list-disc pl-6">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      {draft && (
        <div className="space-y-6">
          <div className="overflow-x-auto border border-neutral-200 rounded-lg">
            <table className="min-w-full">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="text-left text-xs font-semibold uppercase text-neutral-500 px-3 py-2">
                    Tier
                  </th>
                  <th className="text-left text-xs font-semibold uppercase text-neutral-500 px-3 py-2">
                    Min active referrals
                  </th>
                  <th className="text-left text-xs font-semibold uppercase text-neutral-500 px-3 py-2">
                    Share %
                  </th>
                  <th className="text-left text-xs font-semibold uppercase text-neutral-500 px-3 py-2">
                    Sign-up bonus (USDC)
                  </th>
                  <th className="text-left text-xs font-semibold uppercase text-neutral-500 px-3 py-2">
                    Campaign link limit
                  </th>
                </tr>
              </thead>
              <tbody>
                {draft.tiers.map((t, i) => (
                  <tr key={t.name}>
                    <td className="px-3 py-2 text-sm font-medium capitalize border-t border-neutral-100">
                      {t.name}
                    </td>
                    <td className="px-3 py-2 border-t border-neutral-100">
                      <input
                        aria-label={`${t.name} min active referrals`}
                        className={inputCls}
                        type="number"
                        value={t.min_active_referrals}
                        onChange={(e) =>
                          setTier(i, { min_active_referrals: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 border-t border-neutral-100">
                      <input
                        aria-label={`${t.name} share %`}
                        className={inputCls}
                        value={t.share_percent}
                        onChange={(e) =>
                          setTier(i, { share_percent: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 border-t border-neutral-100">
                      <input
                        aria-label={`${t.name} sign-up bonus`}
                        className={inputCls}
                        value={t.signup_bonus_usdc}
                        onChange={(e) =>
                          setTier(i, { signup_bonus_usdc: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-3 py-2 border-t border-neutral-100">
                      <input
                        aria-label={`${t.name} campaign link limit`}
                        className={inputCls}
                        type="number"
                        value={t.campaign_link_limit}
                        onChange={(e) =>
                          setTier(i, { campaign_link_limit: e.target.value })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block text-sm">
              Sign-up minimum deposit (USDC)
              <input
                className={inputCls}
                value={draft.signup_min_deposit_usdc}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    signup_min_deposit_usdc: e.target.value,
                  })
                }
              />
            </label>
            <label className="block text-sm">
              Sign-up bonus hold (days)
              <input
                className={inputCls}
                type="number"
                value={draft.signup_bonus_hold_days}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    signup_bonus_hold_days: e.target.value,
                  })
                }
              />
            </label>
            <label className="block text-sm">
              Attribution window (hours)
              <input
                className={inputCls}
                type="number"
                value={draft.attribution_window_hours}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    attribution_window_hours: e.target.value,
                  })
                }
              />
            </label>
          </div>
        </div>
      )}
    </PartnersLayout>
  )
}

export default ProgramConfig
